<template>
  <q-field
    v-bind="$attrs"
    ref="fieldRef"
    :label="$t('label.passenger_info')"
    :rules="[() => !modelValue.email || requiredEmailField(modelValue.email)]"
    hide-bottom-space
    stack-label
  >
    <template #prepend>
      <q-icon
        name="pax_alt"
        size="32px"
        color="black"
      />
    </template>
    <div class="flex items-center text-grey-8">
      {{ modelValue.full_name || $t('transfer.i_am_passenger') }}
    </div>
    <q-popup-proxy @before-show="load(modelValue.full_name)">
      <q-card class="full-width">
        <q-card-section class="q-gutter-16">
          <q-select
            ref="selectRef"
            :model-value="form.full_name"
            :label="$t('label.user.specify_a_passenger')"
            input-debounce="300"
            :options="options"
            option-label="full_name"
            use-input
            fill-input
            hide-selected
            @filter="load"
            @popup-show="dialogShown"
            @update:modelValue="onSelect"
            @input-value="form.full_name = $event"
          />
          <q-input
            v-model="form.email"
            type="email"
            :label="$t('label.email')"
            :disable="!form.full_name"
            @update:modelValue="$refs.fieldRef.resetValidation()"
          />
          <IntlTelInput
            ref="phoneInputRef"
            v-model="form.phone"
            :label="$t('label.phone')"
            :disable="!form.full_name"
            hide-bottom-space
          />
          <q-checkbox
            v-model="form.visible"
            :label="$t('label.save_as_passenger')"
            :disable="isDisableSaveBtn"
          />
        </q-card-section>
        <q-card-actions>
          <q-btn
            v-close-popup
            class="full-width"
            color="positive"
            :label="$t('button.add')"
            @click="$emit('save')"
          />
        </q-card-actions>
      </q-card>
    </q-popup-proxy>
    <template
      v-if="modelValue.full_name"
      #append
    >
      <q-icon
        class="q-field__focusable-action"
        name="close"
        size="24px"
        @click.stop="clear"
      />
    </template>
  </q-field>
</template>

<script setup>
import {
  computed,
  inject,
  nextTick,
  ref,
  watch,
} from 'vue';

import IntlTelInput from '~components/IntlTelInput';

import useRequiredFieldRules from '~composables/useRequiredFieldRules';

defineOptions({
  inheritAttrs: false,
});

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({
      full_name: '',
      phone: '',
      email: '',
      visible: false,
    }),
  },
});

const emit = defineEmits(['update:modelValue', 'save']);

const { requiredEmailField } = useRequiredFieldRules();

const axios = inject('axios', undefined);

const form = ref({ ...props.modelValue });
const options = ref([]);
const phoneInputRef = ref(null);
const selectRef = ref(null);

const isDisableSaveBtn = computed(() => options.value.some(
  (el) => ['full_name', 'email', 'phone'].every((key) => props.modelValue[key] === el[key]),
));

async function load(value, update) {
  if (value || (update && !props.modelValue.full_name)) {
    let result = [];
    try {
      result = (await axios('/api/partner_passengers', {
        params: {
          search: value,
          page: 1,
          per_page: 5,
        },
      })).data.partner_passengers;
    } catch (err) {
      console.error(err);
    } finally {
      options.value = result;
      if (update) update();
    }
  } else if (update) {
    update();
  }
}

function onSelect({
  full_name, phone, email, visible,
}) {
  form.value = {
    full_name,
    phone,
    email,
    visible,
  };
  phoneInputRef.value?.setPhone(phone);
}

function clear() {
  onSelect({
    full_name: '',
    phone: '',
    email: '',
    visible: true,
  });
}

function dialogShown() {
  nextTick(() => {
    selectRef.value.$el.focus();
  });
}

watch(form, (value) => {
  emit('update:modelValue', value);
}, { deep: true });

watch(() => form.value.full_name, (value) => {
  if (!value) {
    form.value = {
      full_name: '',
      phone: '',
      email: '',
      visible: false,
    };
    phoneInputRef.value?.setPhone('');
  }
});
</script>
